import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1732495522010-e0db10e3a56c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxyb21hbnRpYyUyMGJsdXNoJTIwZXZlbnQlMjBkZWNvciUyMHRhYmxlc2NhcGV8ZW58MHx8fHwxNzkxNDY3ODQxfDA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full grid lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <MaskedLine delay={0.15}>
            <span className="eyebrow" data-testid="hero-eyebrow">
              Event Styling · Balloon Garlands · Private Chef Catering
            </span>
          </MaskedLine>
          <h1 className="mt-8 font-serif font-light leading-[0.95] tracking-tight text-[#2A2423]">
            <MaskedLine delay={0.3} className="text-6xl sm:text-7xl lg:text-8xl">
              The Diamond
            </MaskedLine>
            <MaskedLine delay={0.45} className="text-6xl sm:text-7xl lg:text-8xl">
              Effect <em className="text-[#D8A7A1] not-italic font-serif italic">Affairs</em>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.7}>
            <p className="mt-10 max-w-md text-base leading-relaxed text-muted-foreground" data-testid="hero-subtext">
              Where every gathering becomes a jewel. Bespoke decor, cascading balloon garlands, and a private chef
              crafting Caribbean, Asian &amp; American cuisine — for birthdays, anniversaries, picnics and intimate dinners.
            </p>
          </MaskedLine>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <a
              href="#booking"
              data-testid="hero-cta-book"
              className="bg-[#2A2423] text-[#FDFBF7] text-xs uppercase tracking-[0.22em] px-9 py-4 rounded-full hover:bg-[#D8A7A1] transition-colors duration-300"
            >
              Plan Your Event
            </a>
            <a
              href="#catering"
              data-testid="hero-cta-catering"
              className="text-xs uppercase tracking-[0.22em] text-foreground border-b border-[#D8A7A1] pb-1 hover:text-[#D8A7A1] transition-colors duration-300"
            >
              Explore Catering
            </a>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="rounded-t-full overflow-hidden border border-[#E5D3B3] aspect-[3/4]">
              <motion.img
                src={HERO_IMG}
                alt="Romantic candle-lit event tablescape in blush tones"
                data-testid="hero-image"
                style={{ y: imgY }}
                className="w-full h-[115%] object-cover scale-105"
              />
            </div>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              className="absolute -bottom-8 -left-8 w-28 h-28 hidden lg:flex items-center justify-center"
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <defs>
                  <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                </defs>
                <text className="fill-[#D8A7A1]" style={{ fontSize: "10.5px", letterSpacing: "2.5px", textTransform: "uppercase" }}>
                  <textPath href="#circlePath">curated with love · styled to shine ·</textPath>
                </text>
              </svg>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#services"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground"
        aria-label="Scroll to services"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }} className="block">
          <ArrowDown className="w-4 h-4" />
        </motion.span>
      </motion.a>
    </section>
  );
}
